import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import {
  loadServerPreferences,
  saveServerPreferences,
  type PreferenceSync,
} from './preferences-api';

const STORAGE_PREFIX = 'quality.vet:accessibility-display:';

function preferenceKey(userKey: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(userKey)}`;
}

function readLocalPreference(userKey: string): boolean {
  try {
    return window.localStorage.getItem(preferenceKey(userKey)) === 'enhanced';
  } catch {
    return false;
  }
}

function writeLocalPreference(userKey: string, enabled: boolean): void {
  try {
    if (enabled) {
      window.localStorage.setItem(preferenceKey(userKey), 'enhanced');
    } else {
      window.localStorage.removeItem(preferenceKey(userKey));
    }
  } catch {
    // The server remains authoritative if browser storage is unavailable.
  }
}

function applyAccessibilityDisplay(enabled: boolean): void {
  if (enabled) {
    document.documentElement.dataset.accessibilityDisplay = 'enhanced';
  } else {
    delete document.documentElement.dataset.accessibilityDisplay;
  }
}

/**
 * The persisted server preference is authoritative. The small, non-sensitive
 * local mirror lets the authenticated workspace render in the preferred mode
 * before the preference read completes on a recognised device.
 */
export function useAccessibilityDisplayPreference(
  userKey: string | undefined,
  sync?: PreferenceSync,
) {
  const [accessibilityDisplayEnabled, setAccessibilityDisplayState] = useState(false);

  useLayoutEffect(() => {
    const enabled = userKey ? readLocalPreference(userKey) : false;
    setAccessibilityDisplayState(enabled);
    applyAccessibilityDisplay(enabled);
  }, [userKey]);

  useEffect(() => {
    if (!userKey || !sync?.accessToken) return;
    let active = true;
    void loadServerPreferences(sync).then((saved) => {
      if (!active || !saved) return;
      const enabled = saved.accessibilityDisplayEnabled === true;
      setAccessibilityDisplayState(enabled);
      applyAccessibilityDisplay(enabled);
      writeLocalPreference(userKey, enabled);
    });
    return () => {
      active = false;
    };
  }, [sync?.accessToken, sync?.apiUrl, userKey]);

  const setAccessibilityDisplayEnabled = useCallback(
    async (enabled: boolean) => {
      if (!userKey || !sync?.accessToken) {
        throw new Error('Sign in again before changing the accessibility display.');
      }
      const previous = accessibilityDisplayEnabled;
      setAccessibilityDisplayState(enabled);
      applyAccessibilityDisplay(enabled);
      writeLocalPreference(userKey, enabled);
      try {
        const saved = await saveServerPreferences(sync, { accessibilityDisplayEnabled: enabled });
        const confirmed = saved.accessibilityDisplayEnabled === true;
        setAccessibilityDisplayState(confirmed);
        applyAccessibilityDisplay(confirmed);
        writeLocalPreference(userKey, confirmed);
      } catch (error) {
        setAccessibilityDisplayState(previous);
        applyAccessibilityDisplay(previous);
        writeLocalPreference(userKey, previous);
        throw error;
      }
    },
    [accessibilityDisplayEnabled, sync, userKey],
  );

  return { accessibilityDisplayEnabled, setAccessibilityDisplayEnabled };
}
